import type { AuthHandshake } from '@pin/schemas/socket';
import * as Sentry from '@sentry/svelte';
import { io, type Socket } from 'socket.io-client';

interface AdminSocketOpts {
  /**
   * The moderator's Firebase ID token, null when nobody is signed in.
   * Rejects when the token cannot be fetched.
   */
  getIdToken: () => Promise<string | null>;
  /**
   * Wait before connecting again after the token fetch failed or admin-api
   * refused the socket
   */
  retryMs?: number;
}

/**
 * Connects to admin-api with the moderator's Firebase ID token.
 *
 * When the token refresh fails, as it can on a network that is waking up,
 * the socket must not connect without a token: the /overlay namespace takes
 * that as a public viewer, and the presenter feed stops updating with no
 * sign of it. The socket gives up the attempt instead and tries again in
 * `retryMs`, as it does when admin-api refuses it.
 */
export function connectAdminSocket<S extends object, C extends object>(
  url: string,
  { getIdToken, retryMs = 5_000 }: AdminSocketOpts
): Socket<S, C> {
  let retry: ReturnType<typeof setTimeout> | undefined;
  let reported = false;
  let attempts = 0;
  // Report once per outage, so a failure that lasts is seen but not
  // reported every few seconds
  const retryLater = (err: unknown) => {
    if (!reported) Sentry.captureException(err);
    reported = true;
    retry = setTimeout(() => socket.connect(), retryMs);
  };

  const socket: Socket<S, C> = io(url, {
    path: '/chat',
    auth: (cb) => {
      // A token fetch can outlast its connection attempt. Only the latest
      // attempt may connect or give up.
      const attempt = ++attempts;
      getIdToken().then(
        (idToken) => {
          if (attempt !== attempts) return;
          cb({ idToken, accessToken: null } satisfies AuthHandshake);
        },
        (err) => {
          if (attempt !== attempts || !socket.active) return;
          disconnect();
          retryLater(err);
        }
      );
    },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 2000,
    timeout: 3000,
  });

  socket.on('connect', () => {
    reported = false;
  });
  // Transport errors are retried by Socket.IO. A refusal from the server
  // stops the socket.
  socket.on('connect_error', (err) => {
    if (!socket.active) retryLater(err);
  });
  // A socket the page has closed stays closed
  const disconnect = socket.disconnect.bind(socket);
  socket.disconnect = () => {
    clearTimeout(retry);
    return disconnect();
  };
  return socket;
}
